import React from 'react';
import ReactDOM from 'react-dom';
import PouchDB from 'pouchdb';
import localforage from 'localforage';
import baseClient from 'src/client.webstore/api/base';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import qs from 'querystring';
import productsClient from 'src/client.webstore/api/product';
import analytics, {ECOMM_MODIFY_CART_KEYS} from 'src/client.webstore/utils/analytics';
import inventoryClass from 'src/client.webstore/utils/inventoryClass';
import _ from 'lodash';

const cartItems = new PouchDB('cartItems');
const cartConfig = localforage.createInstance({
    name: 'cartConfig'
});

const Analytics = analytics();

let target = document.querySelector('div#cart-toaster');
if (!target) {
    target = document.createElement('div');
    target.id = 'cart-toaster';
    document.body.appendChild(target);

    ReactDOM.render(
        <ToastContainer/>,
        target
    );
}

/**
 * The namespace representing the shopping cart. All static members of this namespace
 * allow for the manipulation of a users shopping cart and aid in the checkout.
 *
 * @memberOf utils
 * @namespace
 * @example
 * cart.setCoupon('summersale2020').then(() => alert('Your coupon has been updated!'));
 *
 * cart.setBilling({
 *     name: 'Kobe Bryant'
 * }).then(() => alert('Your billing information has been updated!'));
 *
 */
const cart = {
    /**
     * Gets a properly formatted payload for `CartCalculationService`
     *
     * @async
     * @return {Promise<{paymentToken: (Object|string), cartCoupon: string, shipping: Object, shippingMethod: (Object|null), cartItems: Array, billing: Object}>}
     */
    getPayload: async () => {
        const cartItemsList = await cart.get();
        return {
            billing: await cart.getBilling(),
            shipping: await cart.getShipping(),
            shippingMethod: await cart.getShippingMethod(),
            cartItems: cartItemsList,
            paymentToken: await cart.getPaymentToken(),
            cartCoupon: await cart.getCoupon(),
            comment: await cart.getComment(),
            currencyMismatch: cartItemsList.some(item => item.currencyMismatch === true)
        };
    },

    /**
     * @return {Promise<Array>} Returns a promise that resolves to an array of objects containing cart item details.
     */
    get: async () => {
        const docs = await cartItems.allDocs({ include_docs: true });
        return docs.rows
            .map(d => d.doc)
            .filter(item =>
                typeof item.catalogId === 'number' && !isNaN(item.catalogId) &&
                typeof item.foreignKeyId === 'number' && !isNaN(item.foreignKeyId)
            );
    },

    /**
     * Calculate the totals for the cart based on submitted information.
     * @return {Promise<Object>}
     */
    calculate: async () => {
        return baseClient()
            .post(
                '/webstore/webstore_cart/calculate',
                null,
                await cart.getPayload()
            )
            .then(res => res.data)
            .catch(e => cart.clear().then(() => toast.error('One or more of the products in your shopping cart are no longer available and have been removed.')));
    },

    /**
     * Calculate the estimated shipping rate
     * @returns {Promise<Object>}
     */
    estimateShipping: async () => {
        let cost = await cartConfig.getItem('estimatedShippingCost') || {};
        const estimatedShippingAddress = await cart.getEstimatedShipping();
        const hash = await hashEstimatedShipping(_.omit(estimatedShippingAddress, 'h'));

        // Use the estimate we calculated already, if available
        if (typeof cost === 'object' && _.has(cost, hash) && _.isNumber(cost[hash])) {
            return cost[hash];
        } else if (_.isEmpty(_.trim(_.get(estimatedShippingAddress, 'zip', '')))) {
            return null;
        }

        // Retrieve a new estimate
        let payload = await cart.getPayload();
        payload.estimatedShipping = estimatedShippingAddress;

        return baseClient()
            .post(
                '/webstore/webstore_cart/estimate_shipping',
                null,
                payload
            )
            .then(res => {
                // Save a map of the costs we've already calculated so we don't bother fetching them again
                cost[hash] = Number(res.data);
                cartConfig.setItem('estimatedShippingCost', cost);
                return res.data;
            })
            .catch(e => toast.error('An estimated shipping rate could not be determined.'));
    },

    /**
     * Empty the cart of all items and clear all user data.
     * @return {Promise<*>}
     */
    clear: () => {
        // PSSD-3334 — drop the sticky Whole Goods flag on cart.clear() so the next
        // order (placed on the same tab) starts with a fresh signal. Without this,
        // a customer who completes one WG order and then browses for a parts order
        // would stay routed to the WG inventory search.
        try { window.sessionStorage.removeItem('pss_cart_has_wg'); }
        catch (e) { /* ignore */ }
        return Promise.all([
            cartConfig.clear(),
            cart.get().then(cartItems => cartItems.map(item => product(item.catalogId, item.foreignKeyId).remove('', '', true)))
        ]);
    },

    /**
     * @param couponCode
     * @return {Promise<*>}
     */
    setCoupon: couponCode => cartConfig.setItem('cartCoupon', couponCode || null),

    /**
     * @return {Promise<string|null>}
     */
    getCoupon: () => cartConfig.getItem('cartCoupon'),

    /**
     * @param zip
     * @return {Promise<*>}
     */
    setZip: zip => cartConfig.setItem('cartZip', zip || null),

    /**
     * @return {Promise<string|null>}
     */
    getZip: () => cartConfig.getItem('cartZip'),

    /**
     * @param {Object} user
     * @return {Promise<*>}
     */
    setBilling: user => cartConfig.setItem('billing', user),

    /**
     * @return {Promise<Object|null>}
     */
    getBilling: () => cartConfig.getItem('billing'),

    /**
     * PSSD-3741: the optional customer checkout comment. Persisted with the cart so it rides
     * along in getPayload() on every order-creating request — including the return leg of a
     * full-page redirect gateway (PayPal / MercuryPay). Cleared with the cart on order success.
     * @param {string} comment
     * @return {Promise<*>}
     */
    setComment: comment => cartConfig.setItem('comment', comment || null),

    /**
     * @return {Promise<string|null>}
     */
    getComment: () => cartConfig.getItem('comment'),

    /**
     * @param {Object} shippingAddress
     * @return {Promise<*>}
     */
    setShipping: shippingAddress => cartConfig.setItem('shipping', shippingAddress),

    /**
     * @return {Promise<Object|null>}
     */
    getShipping: () => cartConfig.getItem('shipping'),

    /**
     * @param {Object} shippingAddress
     * @return {Promise<*>}
     */
    setEstimatedShipping: async shippingAddress => {
        // Keep a hash to track changes to the estimated shipping address or cart items
        shippingAddress['h'] = await hashEstimatedShipping(_.omit(shippingAddress, 'h'));
        cartConfig.setItem('estimatedShipping', shippingAddress);
    },

    /**
     * @return {Promise<Object|null>}
     */
    getEstimatedShipping: () => cartConfig.getItem('estimatedShipping'),

    /**
     * @param {Object|null} shippingMethod An object containing the shipment and rate to use, or null for none
     *  - reference_id - The reference ID to the rate
     *  - shipment_package_id - The reference ID to the shipment package
     * @return {Promise<*>}
     */
    setShippingMethod: shippingMethod => cartConfig.setItem('shippingMethod', shippingMethod),

    /**
     * @return {Promise<Object|null>}
     */
    getShippingMethod: () => cartConfig.getItem('shippingMethod'),

    /**
     * @param {Object|string} token
     * @return {Promise<*>}
     */
    setPaymentToken: token => cartConfig.setItem('paymentToken', token),

    /**
     * @return {Promise<null|Object|string>}
     */
    getPaymentToken: () => cartConfig.getItem('paymentToken'),

    /**
     * Retrieves all applicable shipping rates based on cart data
     * @return {Promise<Array>}
     */
    shippingRates: async () => {
        return baseClient()
            .post(
                '/webstore/webstore_cart/rates',
                null,
                await cart.getPayload()
            )
            .then(res => res.data)
            .catch(e => {
                throw e;
            });
    },

    /**
     * @param params
     */
    addOem: params => {
        let pleaseWait = toast.info('Please wait, checking availability. This may take some time.', {
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false
        });

        params = qs.parse(params);

        baseClient()
            .post('/webstore/webstore_oem_parts/verify', {}, params)
            .then(
                res => {
                    let {catalogId, foreignKeyId, webstoreProduct} = res.data;
                    const item = product(catalogId, foreignKeyId);
                    return item.add(Number(params.qty_1))
                        .then(() => item.settings(_.pick(_.get(webstoreProduct, 'WebstoreProduct'), ['pickup_in_store'])));
                },
                err => {
                    toast.dismiss();
                    toast.error(`${params.addItem_1} is not currently available.`);
                    throw err;
                }
            )
            .then(res => toast.dismiss(pleaseWait))
            .catch(err => console.error(err));
    }
};

let WebstoreComponents;

/**
 *
 * @param catalogId
 * @param foreignKeyId
 * @returns {Object} An object exposing add/remove/update/settings for this cart line item.
 */
const product = (catalogId, foreignKeyId) => {
    const documentId = `${catalogId}-${foreignKeyId}`;
    let options = {
        pickup_in_store: false
    };

    return {
        /**
         *
         * @param quantity
         * @param currency
         * @param salePrice
         * @param currencyMismatch
         * @returns {Promise<PouchDB.Core.Response>|*}
         */
        // `silent` (PSSD-3016): callers inside the one-page checkout pass true to
        // suppress the legacy "Your cart has been updated" toast and the cart
        // side-sheet mount. Both are noise when the user is already viewing the
        // cart in the OrderSummary panel.
        add: (quantity, currency = '', salePrice = '', currencyMismatch = false, silent = false) => {
            // PSSD-3094: quantity 0 is a removal, not an add. Delegate to remove()
            // so the cart actually mutates and a single remove_from_cart fires on
            // success. (Previously this called cart.remove(), which does not exist
            // on the cart namespace and threw a TypeError.)
            if (quantity === 0) {
                return product(catalogId, foreignKeyId).remove(currency, salePrice);
            }

            // Best-effort enrichment for the analytics payload + sticky WG flag.
            // A failure here must never suppress the add_to_cart event (PSSD-3094:
            // the event was previously gated on this fetch and silently dropped
            // when it failed on some sites/products).
            const variationPromise = productsClient().getVariation(catalogId, foreignKeyId)
                .then(res => res.data)
                .catch(() => ({}));

            // PSSD-3334 — sticky Whole Goods flag, independent of analytics timing.
            // Truthy check (not === true): backend returns is_wg as a MySQL TINYINT.
            variationPromise.then(product => {
                if (product && product.is_wg) {
                    try { window.sessionStorage.setItem('pss_cart_has_wg', '1'); }
                    catch (e) { /* ignore — fall back to cart-level signal only */ }
                }
            });

            if (!silent) {
                if (!WebstoreComponents) {
                    WebstoreComponents = new window.PSS.WebstoreComponents();
                }

                WebstoreComponents.mountCartSideSheet();
            }

            return cartItems
                .get(documentId)
                .then(doc => {
                    doc.quantity = quantity;
                    doc.currencyMismatch = currencyMismatch;
                    return cartItems.put(doc);
                })
                .catch(err => {
                    if (err.status !== 404) {
                        throw err;
                    }

                    return cartItems.put({
                        _id: documentId,
                        catalogId: Number(catalogId),
                        foreignKeyId: Number(foreignKeyId),
                        quantity: Number(quantity),
                        currencyMismatch: currencyMismatch
                    });
                })
                // PSSD-3094 AC-1/AC-4: fire add_to_cart ONLY after the cart write
                // resolves. If the write rejects, this chain rejects and no event
                // fires.
                .then(() => variationPromise)
                .then(product => {
                    Analytics.action({
                        ecomm_part_detail_id: [`${product.webstore_product_id ?? foreignKeyId}`],
                        ecomm_part_detail_name: [_.get(product, 'ProductGroup.name', '')],
                        ecomm_part_detail_variant: [_.trim(`${_.get(product, 'size', '')} ${_.get(product, 'color', '')} ${_.get(product, 'option', '')}`)],
                        ecomm_part_detail_model: [_.get(product, 'manufacturer_part_number', '')],
                        ecomm_part_detail_group_id: [_.get(product, 'ProductGroup.id', '')],
                        ecomm_part_detail_inventory_class: [inventoryClass(catalogId, product)],
                        ecomm_cart_event: 'add_to_cart',
                        ecomm_cart_quantity_added_removed: [quantity],
                        ecomm_part_currency_code: currency || _.get(window, 'PSS.WebstoreComponents.config.cart.currency', ''),
                        ecomm_part_detail_price: [salePrice],
                        tealium_event: 'ecommerce_part_modify_cart'
                    }, ECOMM_MODIFY_CART_KEYS);

                    if (!silent) {
                        toast.success('Your cart has been updated.');
                    }
                });
        },
        /**
         *
         * @param {string} [currency=''] Currency code passed through to the analytics payload.
         * @param {string|number} [salePrice=''] Sale price passed through to the analytics payload.
         * @param {boolean} [silent=false] When true, suppresses the remove_from_cart analytics event.
         *   Used by cart.clear() (PSSD-3094 Task 3) so order completion / error recovery do not emit a burst of removes.
         * @returns {Promise<PouchDB.Core.Response | boolean>}
         */
        remove: (currency = '', salePrice = '', silent = false) => {
            // Best-effort enrichment for the analytics payload + the per-MPN
            // counter reset. A failure here must never gate the removal.
            const variationPromise = productsClient().getVariation(catalogId, foreignKeyId)
                .then(res => res.data)
                .catch(() => ({}));

            // Reset the per-MPN click counter that createSecureProduct.js keeps in
            // localStorage (PSSD-3334). Kept independent of the removal/silent path
            // to preserve existing behavior.
            variationPromise.then(product => {
                if (product && product.manufacturer_part_number) {
                    try { localStorage.removeItem(product.manufacturer_part_number); }
                    catch (e) { /* ignore */ }
                }
            });

            return cartItems
                .get(documentId)
                .then(doc => {
                    // Capture the quantity being removed BEFORE the doc is gone, so the
                    // modify_cart event can report ecomm_cart_quantity_added_removed on
                    // the remove path too (PSSD-3724).
                    const removedQuantity = doc.quantity;
                    return cartItems.remove(doc).then(removed => ({removed, removedQuantity}));
                })
                // PSSD-3094 AC-3: fire remove_from_cart ONLY after a genuine removal,
                // and only when not silent (cart.clear() will pass silent=true — see
                // PSSD-3094 Task 3 — so order completion / error recovery do not emit
                // a burst of removes).
                .then(({removed, removedQuantity}) => {
                    if (silent) {
                        return removed;
                    }
                    return variationPromise.then(product => {
                        Analytics.action({
                            ecomm_part_detail_id: [`${product.webstore_product_id ?? foreignKeyId}`],
                            ecomm_part_detail_name: [_.get(product, 'ProductGroup.name', '')],
                            ecomm_part_detail_variant: [_.trim(`${_.get(product, 'size', '')} ${_.get(product, 'color', '')} ${_.get(product, 'option', '')}`)],
                            ecomm_part_detail_model: [_.get(product, 'manufacturer_part_number', '')],
                            ecomm_part_detail_group_id: [_.get(product, 'ProductGroup.id', '')],
                            ecomm_part_detail_inventory_class: [inventoryClass(catalogId, product)],
                            ecomm_cart_quantity_added_removed: [removedQuantity],
                            ecomm_part_currency_code: currency || _.get(window, 'PSS.WebstoreComponents.config.cart.currency', ''),
                            ecomm_part_detail_price: [salePrice],
                            ecomm_cart_event: 'remove_from_cart',
                            tealium_event: 'ecommerce_part_modify_cart'
                        }, ECOMM_MODIFY_CART_KEYS);
                        return removed;
                    });
                })
                .catch(err => {
                    if (err.status !== 404) {
                        throw err;
                    }
                    // Item wasn't in the cart — nothing removed, so no event.
                    return true;
                });
        },
        /**
         *
         * @param quantity
         * @returns {Promise<PouchDB.Core.Response>|*}
         */
        update: (quantity) => this.add(quantity),
        /**
         * Defines settings for the product in the cart
         *
         * @param {Object} settings
         * @returns {Promise<PouchDB.Core.Response>|*}
         */
        settings: (settings) => {
            options = _.merge(options, settings || {});
            return cartItems
                .get(documentId)
                .then(doc => {
                    doc.settings = options;
                    cartItems.put(doc);
                })
                .catch(err => {
                    if (err.status !== 404) {
                        throw err;
                    }

                    return Promise.resolve(true);
                });
        }
    };
};

/**
 * Retrieves a hex string hash digest of the given shipping address and current cart items
 *
 * @param shippingAddress {Object}
 * @returns {Promise<string>}
 */
const hashEstimatedShipping = async shippingAddress => {
    let hash = '';

    // Hash the estimated shipping address info and current cart items
    if (typeof TextEncoder !== 'undefined'
        && typeof window.crypto !== 'undefined'
        && typeof window.crypto.subtle !== 'undefined'
        && typeof window.crypto.subtle.digest === 'function'
    ) {
        // Use WebCrypto API to create a hash of the estimated shipping address and the items so we can check whether it changes
        const data = new TextEncoder('utf-8').encode(JSON.stringify(_.merge({}, await cart.get(), shippingAddress)));
        // Convert the digest byte array hash to a hex string for easier reference
        const buffer = await window.crypto.subtle.digest('sha-1', data);
        hash = Array.from(new Uint8Array(buffer)).map(c => c.toString(16).padStart(2, '0')).join('');
    }

    return hash;
};

export {cart, product};